/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOpenAttachmentRevisions';

const ActionOpenAttachmentRevisionsManager: ActionManagerFn<
  'ActionOpenAttachmentRevisions'
> = (platform: Platform): ActionManager<'ActionOpenAttachmentRevisions'> => {
  return {
    ...baseActionManager(platform, 'ActionOpenAttachmentRevisions'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Attachment Revisions ItemID',
        type: 'ItemID',
        initialFilter: 'AttachmentRevisions',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'AttachmentRevisions') {
            return `Item id '${value}' doesn't belong to a Attachment Revisions`;
          }
          return '';
        },
      },
    },
    name: 'Open Attachment Revisions',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'paperclip' },
    description: `**Overview**<br/>
The Open Attachment Revisions action is a specific UI interaction component designed to trigger the display of an "Attachment Revisions" component. This target component is typically a specialized modal or panel used to view the history, previous versions, or changelogs of a specific file or document.<br/><br/>
This action acts as the "Switch" to turn on the revision history view. It is tightly coupled with the AttachmentRevisions component type, employing strict validation to ensure it only targets that specific UI element. It allows developers to keep the revision history hidden by default and only reveal it when the user explicitly requests it (e.g., by clicking a "History" button).

**Key Features**<br/>
* **Targeted Trigger:** Specifically designed to activate an AttachmentRevisions component, distinguishing it from generic "Show" actions.
* **Strict Type Validation:** Implements validateItemId to ensure the target ID corresponds exactly to an 'AttachmentRevisions' component, preventing configuration errors.
* **Registry Filtering:** The property picker uses initialFilter: 'AttachmentRevisions' to streamline configuration, showing only valid targets in the dropdown.
* **Visual Context:** The editor displays the targetItemId directly in the action list, making it clear which revision panel is being triggered.

**Properties**<br/>
* **Attachment Revisions ItemID (targetItemId):** A property of type ItemID that identifies the specific Attachment Revisions component to open.
  * *Validation:* Checks if the item exists and is of type AttachmentRevisions.

**Use Cases**<br/>
* **Document Management:** A "Version History" button next to a contract file that, when clicked, opens the revision panel to show who edited the file last.
* **Audit Trails:** Allowing an administrator to view previous uploads of a specific compliance document.`,
  };
};

export default ActionOpenAttachmentRevisionsManager;
